/**
 * CloseRoleModal.jsx — Employer v1.1 Phase 0.6 Slice D.
 *
 * Confirmation modal wrapping POST /api/employer/roles/{id}/close.
 * Two distinct surfaces driven by the role's current `status`:
 *
 *   - status='active' | 'live' → SOFT-CLOSE (immediate=false)
 *     transitions the row to `closing`. Active matches pause while
 *     in-flight work completes; downstream automation finalises it
 *     to `closed`.
 *
 *   - status='closing' → HARD-CLOSE (immediate=true)
 *     finalises the row to `closed` immediately. Once closed it
 *     cannot be re-opened; only archive remains.
 *
 * Buttons are `disabled` while the request is in flight (W-3 guard).
 * Backend error `detail` is surfaced verbatim inside the modal.
 */
import { Loader2, ShieldAlert, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Operator-locked copy. Tests pin the substrings.
export const SOFT_CLOSE_COPY =
  'Soft-close pauses new matching but keeps the row visible while in-flight work completes. You can hard-close it later, or it will finalise to `closed` automatically.';
export const HARD_CLOSE_COPY =
  'Hard-close finalises the role to `closed` immediately. Active matches stop. You can archive it later, but a closed role cannot be re-opened.';

export default function CloseRoleModal({
  open,
  status,        // 'active' | 'live' | 'closing'
  onCancel,
  onConfirm,     // (immediate: bool) => Promise<void>
  pending,
  error,
}) {
  const hard  = status === 'closing';
  const title = hard ? 'Finalise close (hard-close)?' : 'Soft-close this role?';
  const copy  = hard ? HARD_CLOSE_COPY : SOFT_CLOSE_COPY;
  const cta   = hard ? 'Close now' : 'Close role';

  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(o) => { if (!o && !pending) onCancel(); }}
    >
      <DialogContent
        data-testid="close-confirm-modal"
        aria-busy={pending ? 'true' : 'false'}
      >
        <DialogHeader>
          <DialogTitle data-testid="close-confirm-title">{title}</DialogTitle>
          <DialogDescription data-testid="close-confirm-copy">
            {copy}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
            data-testid="close-confirm-error"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
            data-testid="close-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(hard)}
            disabled={pending}
            data-testid="close-confirm-go"
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Closing…
              </>
            ) : (
              <>
                <XCircle className="mr-2 h-4 w-4" />
                {cta}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
